/** Generic Affiliations Picker — powers L2/L3/L4/L5/L6/L7/L8/L9/L10 onboarding.
 *
 *  Fetches its catalogue from `GET /api/meta/affiliations?section={section}`
 *  and renders categorised checkbox rows with an optional/required
 *  membership-number input per row. Server drives:
 *    · groups (per-category)
 *    · min_required          — how many must be ticked to continue
 *    · membership_required   — every ticked row needs a number
 *    · allow_not_registered  — surfaces a mutually-exclusive
 *                              "Not Registered" opt-out row at the bottom
 *
 *  Emits `[{code, membership_number}]` upstream via `onChange`.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckSquare, Square, Handshake, ShieldOff } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const NR_CODE = "NOT_REGISTERED";

export default function AffiliationsPicker({
  section,            // required — 'advertising_agency', 'production_house', …
  value,              // [{code, membership_number}]
  onChange,
  testidPrefix,       // e.g. 'onboard-aff-production-house'
  city = "",          // optional — filters catalogue to city + pan-India rows
  state = "",         // optional — filters by 2-letter state code
  taxonomy = "",      // optional — 'junior_artist' | 'featured_cast' | ...
}) {
  const [catalogue, setCatalogue] = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { section };
    if (city)     params.city = city;
    if (state)    params.state = state;
    if (taxonomy) params.taxonomy = taxonomy;
    axios.get(`${API}/meta/affiliations`, { params })
      .then(({ data }) => setCatalogue(data))
      .catch((err) => {
        console.warn("[AffiliationsPicker] catalogue fetch failed", err);
        setCatalogue(null);
      })
      .finally(() => setLoading(false));
  }, [section, city, state, taxonomy]);

  const rows = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const byCode = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.code, r])),
    [rows]
  );
  const isChecked = (code) => Boolean(byCode[code]);

  const toggle = (code) => {
    if (isChecked(code)) {
      onChange(rows.filter((r) => r.code !== code));
    } else if (code === NR_CODE) {
      // Mutually exclusive — flush all other picks.
      onChange([{ code: NR_CODE, membership_number: "" }]);
    } else {
      // Turning on a normal row while NOT_REGISTERED is on → flush NR.
      const cleaned = rows.filter((r) => r.code !== NR_CODE);
      onChange([...cleaned, { code, membership_number: "" }]);
    }
  };

  const setMembership = (code, membership_number) => {
    onChange(rows.map((r) => (r.code === code ? { ...r, membership_number } : r)));
  };

  if (loading) {
    return (
      <div className="mt-4 text-[11px] text-white/50 tracking-[0.2em] uppercase"
           data-testid={`${testidPrefix}-loading`}>
        Loading affiliations…
      </div>
    );
  }
  if (!catalogue || !catalogue.groups?.length) return null;

  const { groups, min_required: minRequired,
          membership_required: memberRequired } = catalogue;
  const checkedCount = rows.length;
  const meetsMin     = checkedCount >= minRequired;
  const nrPicked     = isChecked(NR_CODE);

  let sectionSuffix;
  if (minRequired > 0 && !meetsMin) {
    sectionSuffix = <span className="text-amber-300">· Select at least {minRequired}</span>;
  } else if (minRequired > 0 && meetsMin) {
    sectionSuffix = <span className="text-emerald-300">· Selected</span>;
  } else {
    sectionSuffix = <span className="text-white/40">· Optional</span>;
  }

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5"
         data-testid={`${testidPrefix}-block`}>
      <div className="flex items-start gap-3">
        <Handshake size={16} className="text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">
            Industry Affiliations {sectionSuffix}
          </div>
          <p className="text-[11px] text-white/50 mt-1 leading-relaxed max-w-xl">
            {minRequired > 0
              ? `Tick every body you're affiliated with. You must select at least ${minRequired} to continue.`
              : "Tick every body you're affiliated with."}
            {" "}Membership number is {memberRequired ? "required" : "optional for now"}.
          </p>
        </div>
        <div className={`text-[10px] tracking-[0.22em] uppercase ${
              meetsMin ? "text-emerald-300/80" : "text-amber-300/80"
            }`}
             data-testid={`${testidPrefix}-count`}>
          {checkedCount} selected
        </div>
      </div>

      <div className="mt-5 space-y-5">
        {groups.map((g) => {
          const isOptOutGroup = g.category === "Opt-Out";
          return (
            <div key={g.category}
                 data-testid={`${testidPrefix}-group-${g.category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2">
                {g.category}
              </div>
              <div className="grid gap-2">
                {g.items.map((it) => {
                  const checked  = isChecked(it.code);
                  const isNR     = it.code === NR_CODE;
                  const disabled = !checked && !isNR && nrPicked;
                  const Icon = checked ? CheckSquare : Square;
                  return (
                    <div key={it.code}
                         data-testid={`${testidPrefix}-row-${it.code}`}
                         className={`rounded-xl border px-3 py-2.5 transition-colors ${
                           checked
                             ? (isNR
                                 ? "border-amber-500/40 bg-amber-500/[0.06]"
                                 : "border-emerald-500/40 bg-emerald-500/[0.06]")
                             : disabled
                               ? "border-white/[0.04] opacity-45"
                               : "border-white/10 hover:border-white/25"
                         }`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          onClick={() => !disabled && toggle(it.code)}
                          disabled={disabled}
                          data-testid={`${testidPrefix}-toggle-${it.code}`}
                          className="flex items-center gap-2 text-left flex-1 min-w-0 disabled:cursor-not-allowed"
                        >
                          {isNR
                            ? <ShieldOff size={16}
                                         className={checked ? "text-amber-300" : "text-white/45"} />
                            : <Icon size={16}
                                    className={checked ? "text-emerald-400" : "text-white/50"} />
                          }
                          <div className="min-w-0">
                            <div className="text-sm text-white/90 truncate">
                              <span className="font-semibold">{it.name}</span>
                              {!isOptOutGroup && (
                                <span className="text-white/40"> — {it.full_name}</span>
                              )}
                            </div>
                            {isOptOutGroup && (
                              <div className="text-[11px] text-white/45 mt-0.5 leading-relaxed">
                                {it.full_name}
                              </div>
                            )}
                          </div>
                        </button>
                        {checked && !isNR && (
                          <input
                            type="text"
                            value={byCode[it.code].membership_number || ""}
                            onChange={(e) => setMembership(it.code, e.target.value)}
                            placeholder={memberRequired ? "Membership # (required)" : "Membership # (optional)"}
                            className={`h-9 rounded-lg bg-black/40 border px-3 text-xs w-56 ${
                              memberRequired && !byCode[it.code].membership_number
                                ? "border-red-500/40"
                                : "border-white/15"
                            }`}
                            data-testid={`${testidPrefix}-membership-${it.code}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
